// src/components/event/MyEvents.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaTrash, FaChartBar, FaShareAlt, FaCopy, FaTimes } from 'react-icons/fa';
import { getEvents, deleteEvent } from '../../services/api';
import './MyEvents.css';

const MyEvents = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [shareableLink, setShareableLink] = useState('');
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [showDeletePasswordDialog, setShowDeletePasswordDialog] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [selectedEventForShare, setSelectedEventForShare] = useState(null);
  const [deletePassword, setDeletePassword] = useState('');
  const [error, setError] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);

  useEffect(() => {
    loadEvents();
  }, []);

  const loadEvents = async () => {
    try {
      const loadedEvents = await getEvents();
      setEvents(loadedEvents);
    } catch (error) {
      console.error('Error loading events:', error);
      setError('Failed to load events');
    }
  };

  const handleShare = (event) => {
    const shareUrl = `${window.location.origin}/evaluate/${event.id}`;
    setShareableLink(shareUrl);
    setSelectedEventForShare(event);
    setShowShareDialog(true);
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareableLink);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const handleDelete = (eventId) => {
    setSelectedEventId(eventId);
    setShowDeleteConfirmation(true);
  };

  const handleDeleteConfirm = () => {
    setShowDeleteConfirmation(false);
    const eventToDelete = events.find(e => e.id === selectedEventId);
    if (eventToDelete?.password) {
      setShowDeletePasswordDialog(true);
    } else {
      deleteEventConfirmed();
    }
  };

  const deleteEventConfirmed = async () => {
    try {
      await deleteEvent(selectedEventId);
      setShowDeleteConfirmation(false);
      setShowDeletePasswordDialog(false);
      setSelectedEventId(null);
      await loadEvents(); // Reload the events list
    } catch (error) {
      setError('Failed to delete event');
      console.error('Delete error:', error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Invalid Date';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Invalid Date';
    }
  };

  return (
    <div className="events-page">
      <div className="page-header">
        <h2>My Dartboards</h2>
        <button 
          onClick={() => navigate('/create')} 
          className="create-button"
        >
          Create New Dartboard
        </button>
      </div>

      {error && <div className="error-banner">{error}</div>}

      <div className="events-grid">
        {events.length === 0 ? (
          <div className="no-events">
            <p>No dartboards created yet. Create your first one!</p>
          </div>
        ) : (
          events.map(event => (
            <div key={event.id} className="event-card">
              <h4>{event.title}</h4>
              <p className="event-date">Created: {formatDate(event.created_at)}</p>
              <div className="event-actions">
                <button
                  onClick={() => navigate(`/event/${event.id}`)}
                  className="icon-button view"
                  title="View Results"
                >
                  <FaChartBar />
                </button>
                <button
                  onClick={() => handleShare(event)}
                  className="icon-button share"
                  title="Share Dartboard"
                >
                  <FaShareAlt />
                </button>
                <button
                  onClick={() => handleDelete(event.id)}
                  className="icon-button delete"
                  title="Delete Dartboard"
                >
                  <FaTrash />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Share Dialog */}
      {showShareDialog && (
        <div className="modal-overlay">
          <div className="modal-dialog share-dialog">
            <button 
              className="close-modal"
              onClick={() => setShowShareDialog(false)}
            >
              <FaTimes />
            </button>
            <h3>Share Dartboard</h3>
            <div className="share-content">
              <div className="share-input-group">
                <input 
                  type="text" 
                  value={shareableLink} 
                  readOnly 
                  className="share-input"
                />
                <button 
                  onClick={handleCopyLink}
                  className="copy-button"
                  title="Copy to clipboard"
                >
                  <FaCopy />
                  {copySuccess ? 'Copied!' : 'Copy'}
                </button>
              </div>
              {selectedEventForShare?.password && (
                <div className="password-info">
                  <p>Password for results: <strong>{selectedEventForShare.password}</strong></p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirmation && (
        <div className="modal-overlay">
          <div className="modal-dialog">
            <h3>Delete Dartboard</h3>
            <p>Are you sure you want to delete this dartboard? This action cannot be undone.</p>
            <div className="dialog-buttons">
              <button 
                onClick={handleDeleteConfirm}
                className="delete-button"
              >
                Delete
              </button>
              <button 
                onClick={() => setShowDeleteConfirmation(false)}
                className="cancel-button"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Password Dialog */}
      {showDeletePasswordDialog && (
        <div className="modal-overlay">
          <div className="modal-dialog">
            <h3>Enter Password</h3>
            <p>Please enter the password to delete this dartboard:</p>
            <input
              type="password"
              value={deletePassword}
              onChange={(e) => setDeletePassword(e.target.value)}
              className="password-input"
              placeholder="Enter password"
            />
            {error && <p className="error-message">{error}</p>}
            <div className="dialog-buttons">
              <button 
                onClick={deleteEventConfirmed}
                className="delete-button"
                disabled={!deletePassword}
              >
                Delete
              </button>
              <button 
                onClick={() => {
                  setShowDeletePasswordDialog(false);
                  setDeletePassword('');
                  setError('');
                }}
                className="cancel-button"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyEvents;
